import type { ReactNode } from "react";

interface SectionProps {
  title: string;
  meta?: string;
  children: ReactNode;
  /** Optional accent border on the left of the section header — used for "today" vs past-day blocks. */
  accent?: "active" | "stale" | null;
}

export function Section({ title, meta, children, accent }: SectionProps) {
  const headBg =
    accent === "active"
      ? "bg-emerald-50"
      : accent === "stale"
      ? "bg-amber-50"
      : "bg-white";
  const metaColor =
    accent === "active" ? "text-emerald-800" : accent === "stale" ? "text-amber-800" : "text-zinc-400";
  const borderColor = accent === "active" ? "border-emerald-300" : "border-zinc-200";
  return (
    <div
      className={`bg-white border ${borderColor} rounded-lg overflow-hidden mb-3`}
    >
      <div
        className={`${headBg} px-3 py-2 border-b border-zinc-200 flex items-center justify-between`}
      >
        <div className="text-[13px] font-semibold text-zinc-900">{title}</div>
        {meta && (
          <div className={`text-[10px] uppercase tracking-wider ${metaColor}`}>{meta}</div>
        )}
      </div>
      {children}
    </div>
  );
}

/** Visual sub-header inside a Section, used to label past-day blocks. */
export function StaleDayHeader({ label }: { label: string }) {
  return (
    <div className="bg-amber-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 border-b border-amber-100">
      {label}
    </div>
  );
}

interface CheckRowProps {
  label: ReactNode;
  sub?: ReactNode;
  /** Right-aligned controls (the buttons). */
  trailing?: ReactNode;
  /** Visual highlight when this row was flagged as an issue. */
  flagged?: boolean;
}

export function CheckRow({ label, sub, trailing, flagged }: CheckRowProps) {
  return (
    <div
      className={`flex items-start gap-3 px-3 py-2.5 border-b border-zinc-100 last:border-b-0 ${
        flagged ? "bg-rose-50" : ""
      }`}
    >
      <div className="flex-1 text-[13px] text-zinc-900">
        <div>{label}</div>
        {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
      </div>
      {trailing}
    </div>
  );
}
